export function isRefundAddedByWeb(
  raw: Record<string, unknown> | null | undefined,
): boolean {
  if (!raw) return false;
  const v = raw.isaddedbyWeb ?? raw.isaddedbyweb ?? raw.IsAddedByWeb;
  return (
    v === true ||
    v === 1 ||
    (typeof v === "string" && v.trim().toLowerCase() === "true")
  );
}

export function getRefundSourceLabel(addedByWeb: boolean): string {
  return addedByWeb ? "Manual" : "System";
}

/** Booking IDs that appear on more than one refund row (conflict). */
export function getConflictBookingMasterIds(
  rows: { bookingMasterId: string | number }[],
): Set<string> {
  const counts = new Map<string, number>();
  for (const row of rows) {
    const id = String(row.bookingMasterId ?? "").trim();
    if (!id) continue;
    counts.set(id, (counts.get(id) ?? 0) + 1);
  }
  const conflicts = new Set<string>();
  counts.forEach((count, id) => {
    if (count > 1) conflicts.add(id);
  });
  return conflicts;
}

export function filterRowsByConflictBookingIds<T extends { bookingMasterId: string | number }>(
  rows: T[],
  conflictBookingIds: Set<string>,
): T[] {
  if (conflictBookingIds.size === 0) return [];
  return rows.filter((row) =>
    conflictBookingIds.has(String(row.bookingMasterId ?? "").trim()),
  );
}

export interface ConflictBookingGroup<T> {
  bookingMasterId: string;
  refunds: T[];
}

/** One row per booking; refunds sorted newest first (by refund id). */
export function groupConflictRefundsByBooking<
  T extends { bookingMasterId: string | number; refundId: string | number },
>(rows: T[]): ConflictBookingGroup<T>[] {
  const map = new Map<string, T[]>();
  for (const row of rows) {
    const id = String(row.bookingMasterId ?? "").trim();
    if (!id) continue;
    const list = map.get(id) ?? [];
    list.push(row);
    map.set(id, list);
  }

  return Array.from(map.entries())
    .map(([bookingMasterId, refunds]) => ({
      bookingMasterId,
      refunds: [...refunds].sort((a, b) => {
        const aId = Number(a.refundId);
        const bId = Number(b.refundId);
        if (Number.isFinite(aId) && Number.isFinite(bId)) return bId - aId;
        return String(b.refundId).localeCompare(String(a.refundId));
      }),
    }))
    .sort(
      (a, b) =>
        b.refunds.length - a.refunds.length ||
        b.bookingMasterId.localeCompare(a.bookingMasterId),
    );
}
